import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import Svg, { Circle, Line, Polyline, Rect } from 'react-native-svg';

import { reviewDesk } from '@/design-system';
import type { CompetencyTimelinePoint } from '../../types/icfProgress';

interface CompetencySparklineProps {
  timeline: CompetencyTimelinePoint[];
  emptyLabel: string;
  accessibilityLabel: string;
  maxPoints?: number;
  height?: number;
}

const WIDTH = 320;
const LEFT = 10;
const RIGHT = 10;
const TOP = 8;
const BOTTOM = 12;
const MIN_ORDINAL = 0;
const MAX_ORDINAL = 4;

export default function CompetencySparkline({
  timeline,
  emptyLabel,
  accessibilityLabel,
  maxPoints = 8,
  height = 88,
}: CompetencySparklineProps) {
  const colors = {
    line: reviewDesk.accent,
    point: reviewDesk.accent,
    grid: reviewDesk.lineStrong,
    accBand: reviewDesk.successSoft,
    pccBand: reviewDesk.accentSoft,
    label: reviewDesk.muted,
  };

  const points = useMemo(
    () => timeline
      .filter((item) => Number.isFinite(item.level_ordinal))
      .slice(-maxPoints),
    [maxPoints, timeline],
  );

  if (points.length === 0) {
    return (
      <View style={{ minHeight: height, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ color: colors.label, fontSize: 12 }}>{emptyLabel}</Text>
      </View>
    );
  }

  const chartWidth = WIDTH - LEFT - RIGHT;
  const chartHeight = height - TOP - BOTTOM;
  const xFor = (index: number) => (
    points.length === 1
      ? LEFT + chartWidth / 2
      : LEFT + (index / (points.length - 1)) * chartWidth
  );
  const yFor = (ordinal: number) => (
    TOP + ((MAX_ORDINAL - Math.max(MIN_ORDINAL, Math.min(MAX_ORDINAL, ordinal))) / (MAX_ORDINAL - MIN_ORDINAL)) * chartHeight
  );
  const polylinePoints = points
    .map((point, index) => `${xFor(index)},${yFor(point.level_ordinal as number)}`)
    .join(' ');

  return (
    <View accessible accessibilityRole="image" accessibilityLabel={accessibilityLabel} style={{ width: '100%', height }}>
      <Svg width="100%" height={height} viewBox={`0 0 ${WIDTH} ${height}`} preserveAspectRatio="none">
        <Rect
          x={LEFT}
          y={yFor(2)}
          width={chartWidth}
          height={yFor(1) - yFor(2)}
          fill={colors.accBand}
          opacity={0.8}
        />
        <Rect
          x={LEFT}
          y={yFor(4)}
          width={chartWidth}
          height={yFor(3) - yFor(4)}
          fill={colors.pccBand}
          opacity={0.85}
        />
        {[1, 3].map((ordinal) => (
          <Line
            key={`grid-${ordinal}`}
            x1={LEFT}
            x2={WIDTH - RIGHT}
            y1={yFor(ordinal)}
            y2={yFor(ordinal)}
            stroke={colors.grid}
            strokeWidth={1}
            opacity={0.7}
          />
        ))}
        {points.length > 1 ? (
          <Polyline
            points={polylinePoints}
            fill="none"
            stroke={colors.line}
            strokeWidth={2.4}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null}
        {points.map((point, index) => (
          <Circle
            key={`${point.session_id || point.timestamp}-${index}`}
            cx={xFor(index)}
            cy={yFor(point.level_ordinal as number)}
            r={3.5}
            fill={reviewDesk.card}
            stroke={colors.point}
            strokeWidth={2.2}
          />
        ))}
      </Svg>
    </View>
  );
}
